// StakeholderRadar2Intro.jsx - EXACT SAME STATIC LAYOUT as Radar1
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import pandaAvatar from "../../../../../../assets/PandaProfile.png";
import { progressService } from "../../../../../../services/progressService";

import Lion from "../../../../../../assets/animals/Lion.png";
import Monkey from "../../../../../../assets/animals/Monkey.png";
import Tiger from "../../../../../../assets/animals/Tiger.png";
import Koala from "../../../../../../assets/animals/Koala.png";
import Fox from "../../../../../../assets/animals/Fox.png";
import Panda from "../../../../../../assets/animals/Piglet.png";
import Cat from "../../../../../../assets/animals/Cat.png";
import Gorilla from "../../../../../../assets/animals/Gorilla.png";
import Hyena from "../../../../../../assets/animals/Hyena.png";

const animalIcons = [Lion, Monkey, Tiger, Koala, Fox, Panda, Cat, Gorilla, Hyena];

const getAnimalImage = (stakeholder, fallbackIndex = 0) => {
  if (stakeholder.animal && typeof stakeholder.animal === "string") {
    return stakeholder.animal;
  }
  return animalIcons[fallbackIndex % animalIcons.length];
};

const StakeholderRadar2Intro = ({ userId, subsectionId, onNextStep }) => {
  const [visibilityMap, setVisibilityMap] = useState({});
  const [selectedStakeholders, setSelectedStakeholders] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [userProfileImage, setUserProfileImage] = useState(null);

  useEffect(() => {
    async function loadUserProfile() {
      try {
        const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
        const profileProgress = await progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID);
        const payload = profileProgress?.data?.payload;
        if (payload?.profileImage) {
          setUserProfileImage(payload.profileImage);
        }
      } catch (err) {
        console.error("Failed to load user profile:", err);
      }
    }
    if (userId) loadUserProfile();
  }, [userId]);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "interpersonal",
          "interpersonal_map"
        );
        if (res?.visibilityMap) setVisibilityMap(res.visibilityMap);

        const response = await fetch(
          `http://127.0.0.1:8000/users/${userId}/progress/${subsectionId}`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        
        if (response.ok) {
          const data = await response.json();
          const selected = data?.data?.payload?.selectedStakeholders;
          if (selected) setSelectedStakeholders(selected);
        }
      } catch (e) {
        console.error("Error loading data:", e);
      }
      setLoading(false);
    }
    if (userId && subsectionId) loadData();
  }, [userId, subsectionId]);

  // ✅ EXACT SAME as Radar1
  const GAP_BETWEEN_CIRCLES = 280;
  const STARTING_RADIUS = 300;
  const circleRings = Array.from({ length: 4 }, (_, i) =>
    STARTING_RADIUS + i * GAP_BETWEEN_CIRCLES
  );

  const polarToCartesian = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
  };

  const getStakeholders = (key) => visibilityMap[key]?.items || [];
  
  const allStakeholders = [
    ...getStakeholders("q1"),
    ...getStakeholders("q2"),
    ...getStakeholders("q3"),
    ...getStakeholders("q4"),
  ];

  const bossStakeholder = allStakeholders.find((s) =>
    (s.relation || "").toLowerCase().includes("boss")
  );

  const targetStakeholder = allStakeholders.find((s) => 
    s.id === selectedStakeholders[1]?.id || 
    s.name === selectedStakeholders[1]?.name ||
    (s.name && selectedStakeholders[1]?.name && s.name.toLowerCase() === selectedStakeholders[1].name.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading...</p>
      </div>
    );
  }

  // ✅ EXACT SAME as Radar1
  const CENTER_X = 150;
  const CENTER_Y = 420;

  // ✅ EXACT SAME as Radar1
  const quadrantToOrbit = {
    q3: 0,
    q4: 1,
    q2: 2,
    q1: 3,
  };
  
  // ✅ EXACT SAME as Radar1
  const quadrantSettings = {
    q3: { angle: 30, leftShift: -70 },
    q4: { angle: 350, leftShift: -70 },
    q2: { angle: 350, leftShift: -70 },
    q1: { angle: 2, leftShift: -40 },
  };

  // ✅ Boss at orbit 2, angle 20°
  const bossPos = polarToCartesian(circleRings[2], 15);
  
  // ✅ Target at orbit 3, angle 2° (outermost)
  const targetPos = polarToCartesian(circleRings[3], 2);

  const LAST_ORBIT_INDEX = circleRings.length - 1;

  return (
    <>
      <motion.div
        className="relative w-full h-[90vh] bg-white overflow-hidden"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* ✅ EXACT SAME header */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center z-50 w-[1300px]">
          <h3 className="font-[Gotham] font-medium text-[28px] leading-[135%] text-center text-[#323232]">
            Now - let's do the same for your other stakeholder.
          </h3>
        </div>

        {/* ✅ EXACT SAME container structure */}
        <div className="absolute left-0 right-0" style={{ overflowY: "hidden", overflowX: "hidden" }}>
          <div
            className="relative mx-auto"
            style={{
              width: "1440px",
              height: "1080px",
              minWidth: "1440px",
              minHeight: "1080px",
            }}
          >
            {/* ✅ Orbit circles */}
            {circleRings.map((r) => (
              <div
                key={r}
                className="absolute rounded-full border border-[#5EB1BF]"
                style={{ 
                  width: `${r * 2}px`, 
                  height: `${r * 2}px`, 
                  left: `150px`, 
                  top: `420px`, 
                  transform: `translate(-${r}px, -${r}px)` 
                }}
              />
            ))}

            {/* ✅ EXACT SAME user profile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute z-30"
              style={{
                left: `${CENTER_X - 70}px`,
                top: `${CENTER_Y - 85}px`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div className="w-[176.55px] h-[176.55px] rounded-full bg-[#E7E7E7] shadow-md overflow-hidden flex-shrink-0">
                <img
                  src={userProfileImage || pandaAvatar}
                  alt="You"
                  className="w-[140.95px] h-[140.95px] object-cover absolute inset-0 m-auto"
                  style={{ borderRadius: "70.47px", opacity: 1 }}
                />
              </div>
            </motion.div>

            {/* ✅ Horizontal line - FIXED WIDTH 920px */}
            {targetStakeholder && (
              <div
                className="absolute flex flex-col items-start z-30"
                style={{
                  left: `${CENTER_X + 600}px`,
                  top: `${CENTER_Y + 20}px`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="relative flex items-center" style={{ width: "900px" }}>
                  <div className="absolute left-0 w-3 h-3 rounded-full bg-[#B0B0B0] -translate-x-1/2" />
                  <div className="w-full h-[2px] bg-[#B0B0B0]" />
                  <div className="absolute right-0 w-3 h-3 rounded-full bg-[#B0B0B0] translate-x-1/2" />
                </div>
                
                <div className="pt-2 w-full flex justify-center">
                  <span className="whitespace-nowrap font-[Gotham] font-medium text-[14px] leading-[135%] uppercase text-black">
                    CHANCES OF VISIBILITY &lt;10%
                  </span>
                </div>
              </div>
            )}

            {/* ✅ All other stakeholders - EXACT SAME logic */}
            {(() => {
              let stakeholderIndex = 0;
              return ["q3", "q4", "q2", "q1"].flatMap((qKey) => {
                const stakeholders = getStakeholders(qKey);
                if (stakeholders.length === 0) return [];
                
                const filtered = stakeholders.filter((s) => {
                  if (bossStakeholder && s.id === bossStakeholder.id) {
                    return false;
                  }
                  if (targetStakeholder && s.id === targetStakeholder.id) {
                    return false;
                  }
                  return true;
                });
                
                if (filtered.length === 0) return [];
                
                const orbitIndex = quadrantToOrbit[qKey];
                const r = circleRings[orbitIndex];
                const settings = quadrantSettings[qKey];
                const n = filtered.length;
                
                return filtered.map((s, idx) => {
                  const isLastOrbit = orbitIndex === LAST_ORBIT_INDEX;
                  const borderTailwind = isLastOrbit
                    ? "border-2 border-[#FF5722]"
                    : "border-[3.48px] border-[#7C7D7E]";
                  const textTailwind = isLastOrbit
                    ? "text-gray-900"
                    : "text-[#7C7D7E]";
                  const itemOpacity = isLastOrbit ? "opacity-100" : "opacity-50";
                  
                  let angle;
                  if (n === 1) {
                    angle = settings.angle;
                  } else {
                    const spread = isLastOrbit ? 50 : 40;
                    const angleStep = spread / (n + 1);
                    angle = settings.angle - spread / 2 + angleStep * (idx + 1);
                  }
                  
                  const { x, y } = polarToCartesian(r, angle);
                  const leftShift = settings.leftShift;
                  const currentIndex = stakeholderIndex++;
                  
                  return (
                    <motion.div
                      key={`${qKey}-${s.id}`}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: currentIndex * 0.08, duration: 0.35 }}
                      className={`absolute flex flex-col items-center text-center z-20 ${itemOpacity}`}
                      style={{
                        left: `${CENTER_X + x + leftShift}px`,
                        top: `${CENTER_Y - y}px`,
                        transform: "translate(-50%, -50%)",
                      }}
                    >
                      <div
                        className={`w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-md ${borderTailwind}`}
                      >
                        <img
                          src={getAnimalImage(s, currentIndex)}
                          alt={s.name}
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <p
                        className={`mt-2 text-sm font-medium whitespace-nowrap font-[Gotham] ${textTailwind}`}
                      >
                        {s.name}
                      </p>
                      <p
                        className={`text-xs whitespace-nowrap font-[Gotham] ${textTailwind}`}
                      >
                        {s.relation}
                      </p>
                    </motion.div>
                  );
                });
              });
            })()}

            {/* ✅ Boss - STATIC at orbit 2, angle 20° */}
            {bossStakeholder && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="absolute flex flex-col items-center text-center z-20"
                style={{
                  left: `${CENTER_X + bossPos.x + quadrantSettings.q1.leftShift - 20}px`,
                  top: `${CENTER_Y - bossPos.y}px`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] flex items-center justify-center shadow-md">
                  <img
                    src={getAnimalImage(bossStakeholder, 0)}
                    alt={bossStakeholder.name}
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <p className="mt-2 text-sm font-medium text-gray-700 whitespace-nowrap font-[Gotham]">
                  {bossStakeholder.name}
                </p>
                <p className="text-xs text-gray-500 whitespace-nowrap font-[Gotham]">
                  {bossStakeholder.relation}
                </p>
              </motion.div>
            )}

            {/* ✅ Target - STATIC at orbit 3, angle 2° */}
            {targetStakeholder && (
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="absolute flex flex-col items-center text-center z-25"
                style={{
                  left: `${CENTER_X + targetPos.x + quadrantSettings.q1.leftShift - 20}px`,
                  top: `${CENTER_Y - targetPos.y}px`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] flex items-center justify-center shadow-md">
                  <img
                    src={getAnimalImage(targetStakeholder, 1)}
                    alt={targetStakeholder.name}
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <p className="mt-2 text-sm font-medium text-gray-700 whitespace-nowrap font-[Gotham]">
                  {targetStakeholder.name}
                </p>
                <p className="text-xs text-gray-500 whitespace-nowrap font-[Gotham]">
                  {targetStakeholder.relation}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ✅ Continue button */}
      <div className="fixed bottom-4 left-1/2 z-50" style={{ left: "calc(50% )", transform: "translateX(-50%)" }}>
        <button
          onClick={onNextStep}
          className="flex items-center justify-center px-8 py-3 bg-[#54B0AF] hover:bg-teal-600 text-white font-medium font-gotham rounded-full shadow-lg transition"
        >
          Next: Stakeholder 2 Profile
        </button>
      </div>
    </>
  );
};

export default StakeholderRadar2Intro;


// import React, { useState, useEffect } from "react";
// import { motion } from "framer-motion";
// import pandaAvatar from "../../../../../../assets/PandaProfile.png";
// import { progressService } from "../../../../../../services/progressService";

// // Import animal images
// import Lion from "../../../../../../assets/animals/Lion.png";
// import Monkey from "../../../../../../assets/animals/Monkey.png";
// import Tiger from "../../../../../../assets/animals/Tiger.png";
// import Koala from "../../../../../../assets/animals/Koala.png";
// import Fox from "../../../../../../assets/animals/Fox.png";
// import Panda from "../../../../../../assets/animals/Piglet.png";
// import Cat from "../../../../../../assets/animals/Cat.png";
// import Gorilla from "../../../../../../assets/animals/Gorilla.png";
// import Hyena from "../../../../../../assets/animals/Hyena.png";

// const animalIcons = [Lion, Monkey, Tiger, Koala, Fox, Panda, Cat, Gorilla, Hyena];

// const getAnimalImage = (stakeholder, fallbackIndex = 0) => {
//   if (stakeholder.animal && typeof stakeholder.animal === "string") {
//     return stakeholder.animal;
//   }
//   return animalIcons[fallbackIndex % animalIcons.length];
// };

// const StakeholderRadar2Intro = ({ userId, subsectionId, onNextStep }) => {
//   const [visibilityMap, setVisibilityMap] = useState({});
//   const [selectedStakeholders, setSelectedStakeholders] = useState([null, null]);
//   const [loading, setLoading] = useState(true);
//   const [userProfileImage, setUserProfileImage] = useState(null);

//   useEffect(() => {
//     async function loadUserProfile() {
//       try {
//         const CURRENT_IDENTITY_SUBSECTION_ID = "f90552a1-12fd-4f0b-b4f5-17e258b84b97";
//         const profileProgress = await progressService.getUserProgress(userId, CURRENT_IDENTITY_SUBSECTION_ID);
//         const payload = profileProgress?.data?.payload;
//         if (payload?.profileImage) {
//           setUserProfileImage(payload.profileImage);
//         }
//       } catch (err) {
//         console.error("Failed to load user profile:", err);
//       }
//     }
//     if (userId) loadUserProfile();
//   }, [userId]);

//   useEffect(() => {
//     async function loadData() {
//       setLoading(true);
//       try {
//         const res = await progressService.getUserDataForSection(
//           userId,
//           "course_career_progression",
//           "interpersonal",
//           "interpersonal_map"
//         );
//         if (res?.visibilityMap) setVisibilityMap(res.visibilityMap);

//         const response = await fetch(
//           `http://127.0.0.1:8000/users/${userId}/progress/${subsectionId}`,
//           { method: "GET", headers: { "Content-Type": "application/json" } }
//         );
        
//         if (response.ok) {
//           const data = await response.json();
//           const selected = data?.data?.payload?.selectedStakeholders;
//           if (selected) setSelectedStakeholders(selected);
//         }
//       } catch (e) {}
//       setLoading(false);
//     }
//     if (userId && subsectionId) loadData();
//   }, [userId, subsectionId]);

//   const GAP_BETWEEN_CIRCLES = 280;
//   const STARTING_RADIUS = 300;
//   const circleRings = Array.from({ length: 4 }, (_, i) =>
//     STARTING_RADIUS + i * GAP_BETWEEN_CIRCLES
//   );

//   const polarToCartesian = (r, deg) => {
//     const rad = (deg * Math.PI) / 180;
//     return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
//   };

//   const getStakeholders = (key) => visibilityMap[key]?.items || [];
  
//   const allStakeholders = [
//     ...getStakeholders("q1"),
//     ...getStakeholders("q2"),
//     ...getStakeholders("q3"),
//     ...getStakeholders("q4"),
//   ];

//   const bossStakeholder = allStakeholders.find((s) =>
//     (s.relation || "").toLowerCase().includes("boss")
//   );

//   const targetStakeholder = allStakeholders.find((s) => 
//     s.id === selectedStakeholders[1]?.id || 
//     s.name === selectedStakeholders[1]?.name ||
//     (s.name && selectedStakeholders[1]?.name && s.name.toLowerCase() === selectedStakeholders[1].name.toLowerCase())
//   );

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-white">
//         <p className="text-gray-500 text-lg">Loading...</p>
//       </div>
//     );
//   }

//   const quadrantToOrbit = {
//     q3: 0,
//     q4: 1,
//     q2: 2,
//     q1: 3,
//   };
  
//   const quadrantSettings = {
//     q3: { angle: 20, leftShift: -20 },
//     q4: { angle: 345, leftShift: 0 },
//     q2: { angle: 350, leftShift: 0 },
//     q1: { angle: 350, leftShift: 0 },
//   };

//   const LAST_ORBIT_INDEX = circleRings.length - 1;

//   return (
//     <motion.div
//       className="relative w-full h-[90vh] bg-white flex items-center overflow-hidden justify-start pl-[28rem]"
//       initial={{ opacity: 0, y: 15 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.3 }}
//     >
//       <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center z-50">
//         <h3 className="font-[Gotham] font-medium text-[32px] leading-[135%] text-center text-[#323232]">
//           Now - let's do the same for your other stakeholder.
//         </h3>
//       </div>

//       <div className="relative flex items-center justify-center">
//         {circleRings.map((r) => (
//           <div
//             key={r}
//             className="absolute rounded-full border border-[#0000001A]"
//             style={{ width: `${r * 2}px`, height: `${r * 2}px` }}
//           />
//         ))}

//         <motion.div
//           initial={{ opacity: 0, scale: 0.8 }}
//           animate={{ opacity: 1, scale: 1 }}
//           transition={{ duration: 0.5 }}
//           className="absolute z-30"
//           style={{
//             left: "-75px",
//             top: "-85px",
//             transform: "translate(-50%, -50%)",
//           }}
//         >
//           <div 
//             className="w-[176.55px] h-[176.55px] rounded-full bg-[#E7E7E7] shadow-md overflow-hidden flex-shrink-0"
//           >
//             <img
//               src={userProfileImage || pandaAvatar}
//               alt="You"
//               className="w-[140.95px] h-[140.95px] object-cover absolute inset-0 m-auto"
//               style={{ 
//                 borderRadius: "70.47px",
//                 opacity: 1 
//               }}
//             />
//           </div>
//         </motion.div>

//         {targetStakeholder && (
//           <div
//             className="absolute flex flex-col items-start z-30"
//             style={{
//               left: "calc(50% + 140px)",
//               top: "50%",
//             }}
//           >
//             <div className="relative flex items-center" style={{ width: "920px" }}>
//               <div className="absolute left-0 w-3 h-3 rounded-full bg-[#B0B0B0] -translate-x-1/2" />
//               <div className="w-full h-[2px] bg-[#B0B0B0]" />
//               <div className="absolute right-0 w-3 h-3 rounded-full bg-[#B0B0B0] translate-x-1/2" />
//             </div>
            
//             <div className="pt-2 w-full flex justify-center">
//               <span className="whitespace-nowrap font-[Gotham] font-medium text-[14px] leading-[135%] uppercase text-black">
//                 CHANCES OF VISIBILITY &lt;10%
//               </span>
//             </div>
//           </div>
//         )}

//         {(() => {
//           let stakeholderIndex = 0;
//           return ["q3", "q4", "q2", "q1"].flatMap((qKey) => {
//             const stakeholders = getStakeholders(qKey);
//             if (stakeholders.length === 0) return [];
            
//             const filtered = stakeholders.filter((s) => 
//               !((s.relation || "").toLowerCase().includes("boss")) &&
//               s.id !== targetStakeholder?.id
//             );
//             if (filtered.length === 0) return [];
            
//             const orbitIndex = quadrantToOrbit[qKey];
//             const r = circleRings[orbitIndex];
//             const settings = quadrantSettings[qKey];
//             const n = filtered.length;
            
//             return filtered.map((s, idx) => {
//               const isLastOrbit = orbitIndex === LAST_ORBIT_INDEX;
//               const borderTailwind = isLastOrbit
//                 ? "border-2 border-[#FF5722]"
//                 : "border-[3.48px] border-[#7C7D7E]";
//               const textTailwind = isLastOrbit
//                 ? "text-gray-900"
//                 : "text-[#7C7D7E]";
//               const itemOpacity = isLastOrbit ? "opacity-100" : "opacity-50";
              
//               let angle;
//               if (n === 1) {
//                 angle = settings.angle;
//               } else {
//                 const spread = 50;
//                 const angleStep = spread / (n + 1);
//                 angle = settings.angle - spread / 2 + angleStep * (idx + 1);
//               }
              
//               const { x, y } = polarToCartesian(r, angle);
//               const leftShift = settings.leftShift;
//               const currentIndex = stakeholderIndex++;
              
//               return (
//                 <div
//                   key={`${qKey}-${s.id}`}
//                   className={`absolute flex flex-col items-center text-center z-20 ${itemOpacity}`}
//                   style={{
//                     left: `${x + leftShift}px`,
//                     top: `${-y}px`,
//                     transform: "translate(-50%, -50%)",
//                   }}
//                 >
//                   <div
//                     className={`w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-md ${borderTailwind}`}
//                   >
//                     <img
//                       src={getAnimalImage(s, currentIndex)}
//                       alt={s.name}
//                       className="w-10 h-10 object-contain"
//                     />
//                   </div>
//                   <p
//                     className={`mt-2 text-sm font-medium whitespace-nowrap font-[Gotham] ${textTailwind}`}
//                   >
//                     {s.name}
//                   </p>
//                   <p
//                     className={`text-xs whitespace-nowrap font-[Gotham] ${textTailwind}`}
//                   >
//                     {s.relation}
//                   </p>
//                 </div>
//               );
//             });
//           });
//         })()}

//         {bossStakeholder && (
//           <div
//             className="absolute flex flex-col items-center text-center z-25"
//             style={{
//               left: `${polarToCartesian(circleRings[2], 15).x + 0}px`,
//               top: `${-polarToCartesian(circleRings[2], 15).y}px`,
//               transform: "translate(-50%, -50%)",
//             }}
//           >
//             <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] flex items-center justify-center shadow-md">
//               <img
//                 src={getAnimalImage(bossStakeholder, 0)}
//                 alt={bossStakeholder.name}
//                 className="w-10 h-10 object-contain"
//               />
//             </div>
//             <p className="mt-2 text-sm font-medium text-gray-700 whitespace-nowrap font-[Gotham]">
//               {bossStakeholder.name}
//             </p>
//             <p className="text-xs text-gray-500 whitespace-nowrap font-[Gotham]">
//               {bossStakeholder.relation}
//             </p>
//           </div>
//         )}

//         {targetStakeholder && (
//           <div
//             className="absolute flex flex-col items-center text-center z-25"
//             style={{
//               left: `${polarToCartesian(circleRings[3], 0).x + 0}px`,
//               top: `${-polarToCartesian(circleRings[3], -1).y}px`,
//               transform: "translate(-50%, -50%)",
//             }}
//           >
//             <div className="w-16 h-16 rounded-full bg-white border-2 border-[#FF5722] flex items-center justify-center shadow-md">
//               <img
//                 src={getAnimalImage(targetStakeholder, 1)}
//                 alt={targetStakeholder.name}
//                 className="w-10 h-10 object-contain"
//               />
//             </div>
//             <p className="mt-2 text-sm font-medium text-gray-700 whitespace-nowrap font-[Gotham]">
//               {targetStakeholder.name}
//             </p>
//             <p className="text-xs text-gray-500 whitespace-nowrap font-[Gotham]">
//               {targetStakeholder.relation}
//             </p>
//           </div>
//         )}
//       </div>

//       <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50">
//         <button
//           onClick={onNextStep}
//           className="flex items-center justify-center px-8 py-3 bg-[#54B0AF] hover:bg-teal-600 text-white font-medium font-gotham rounded-full shadow-lg transition"
//         >
//           Next: Stakeholder 2 Profile
//         </button>
//         {/* <button
//           onClick={onNextStep}
//           className="text-[#323232] text-sm font-gotham font-medium uppercase tracking-wide hover:text-gray-700 transition"
//         >
//           MARK AS COMPLETE →
//         </button> */}
//       </div>
//     </motion.div>
//   );
// };

// export default StakeholderRadar2Intro;